// libraries
import React, { useEffect, useRef, useState } from 'react'

// components
import Section from '/react4xp/components/Section'
import RichText from '/react4xp/components/RichText'
import EventCard from '/react4xp/components/EventCard'
import Announcer, { useAnnouncer, fillPlaceholders } from '/react4xp/components/Announcer'

import dayjs from 'dayjs';

interface Props {
    title?: string
    intro?: string
    endpoint: string
    linkText: string
    futureEvents: any[]
    strings: {
        pastEventsStatus: string // Past events: showing <<shown>>
        noPastEvents: string // No past events found
    }
}

interface eventCard {
    bgColor: 'light' | 'dark'
    img?: {
        src: string
        alt?: string
    }
    startDate: string
    endDate?: string
    title: string
    location?: string
    stand?: string
    href?: {
        text?: string
        url: string
        isExternal?: boolean
    }
}

export default function EventsOverview(props: Props) {
    const { title, intro, endpoint, linkText, futureEvents, strings } = props

    const [pastEvents, setPastEvents] = useState([])
    const [showPastEvents, setShowPastEvents] = useState(false)
    const [isLoadingPast, setIsLoadingPast] = useState(false)
    const [announcement, announce] = useAnnouncer()

    const resultsRef = useRef<HTMLDivElement>(null)
    const focusIndexRef = useRef<number | null>(null)

    async function handleShowPastEvents() {
        if (isLoadingPast) return
        setIsLoadingPast(true)

        let hits = []

        try {
            const res = await fetch(endpoint)
            const json = await res.json()
            hits = json?.hits || []

            setPastEvents(hits)
            // held back: hiding the control first strands focus for the whole request
            setShowPastEvents(true)
        } catch (err) {
            // the control is still there, so leave focus where it is
            console.error(err)
            return
        } finally {
            setIsLoadingPast(false)
        }

        // nothing arrived and the control is gone, so focus goes to the list
        if (!hits.length) {
            resultsRef.current?.focus()
            return announce(strings.noPastEvents)
        }

        focusIndexRef.current = futureEvents?.length || 0
        announce(fillPlaceholders(strings.pastEventsStatus, { shown: hits.length }))
    }

    useEffect(() => {
        const index = focusIndexRef.current
        if (index === null) return
        focusIndexRef.current = null

        const firstPastCard = resultsRef.current?.children[index] as HTMLElement
        firstPastCard?.querySelector('a')?.focus()
    }, [pastEvents])

    return (
        <div className='page--events'>

            <Section bgColor='bg-beige-050' textColor='text-dark' className='top-section'>
                <div className='grid-container'>
                    <div className='grid-row'>
                        <div className='c-md-10 c-lg-8'>
                            <div className='top-section__flex'>

                                <h1 className='title-lg title'>
                                    {title}
                                </h1>

                                <div className='desc'>
                                    <RichText content={intro} />
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </Section>

            <Section bgColor='bg-beige-050' textColor='text-dark' className='all-events' ariaBusy={isLoadingPast}>
                <div className='grid-container'>

                    <Announcer message={announcement} />

                    <div className='grid-row' ref={resultsRef} tabIndex={-1}>

                        {/* display future events */}
                        {futureEvents?.map((item, i) => renderEventCard(i, item, linkText))}

                        {/* display past events if showPastEvents is true */}
                        {pastEvents?.map((item, i) => renderEventCard(i, item, linkText))}
                    </div>

                    {/* show the "load more" button only if there are past events */}
                    {!showPastEvents && (
                        <div className='load-more'>
                            <button
                                onClick={handleShowPastEvents}
                                aria-busy={isLoadingPast ? true : undefined}
                                aria-disabled={isLoadingPast ? true : undefined}
                            >
                                See past events <svg width='14' height='14' fill='currentColor' viewBox='0 0 14 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.6998 0.800049V6.30005H13.1998V7.70005H7.6998V13.2H6.2998V7.70005H0.799805V6.30005H6.2998V0.800049H7.6998Z' /></svg>
                            </button>
                        </div>
                    )}

                </div>
            </Section>
        </div>
    )
}

function renderEventCard(key, item, linkText) {
    return <div className='c-sm-6 c-lg-4' key={key}>
        <EventCard
            bgColor={item.backgroundColor as 'dark' | 'light'}
            img={{
                src: item.image?.link,
                alt: item.image?.alt
            }}
            startDate={item.startDate && dayjs(item.startDate).format('MMMM DD, YYYY')}
            endDate={item.endDate && dayjs(item.endDate).format('MMMM DD, YYYY')}
            title={item.title}
            location={item.location}
            stand={item.stand}
            href={{
                text: linkText,
                url: item.externalLink || item.url?.href,
                isExternal: item.externalLink
            }}
        />
    </div>
}